import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormInput, FormSelect } from "@/components/Base/Form";
import FormTextarea from "@/components/Base/Form/FormTextarea";
import { Dialog } from "@/components/Base/Headless";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { getToken } from "@/utils/auth";

type Team = { id: string; name: string; status?: boolean };
type BankInSummary = {
  teamSalesId: string;
  salesDate: string;
  bankedAt: string | null;
  bankInAmount: number | null;
  bankInDifference: number;
  remainingAmount?: number;
  paidAmount?: number;
  expectedTotal?: number;
  bankInVerified?: boolean;
  bankInVerifiedAt?: string | null;
  receiptCount: number;
  hasReceipt: boolean;
  addedByAdmin?: boolean;
  addedByName?: string | null;
};

const SETTLED_REMAINING_EPS = 0.004;

function remainingAmountOf(summary: BankInSummary): number {
  const remaining = Number(summary.remainingAmount);
  if (Number.isFinite(remaining)) return Math.max(0, remaining);
  // Fallback for older API payloads: bankInDifference = bankInAmount - expectedTotal
  return Math.max(0, -Number(summary.bankInDifference || 0));
}

function isBankInFullySettled(summary: BankInSummary): boolean {
  return remainingAmountOf(summary) <= SETTLED_REMAINING_EPS;
}

function paidAmountOf(summary: BankInSummary): number {
  const paid = Number(summary.paidAmount);
  if (Number.isFinite(paid)) return Math.max(0, paid);
  const banked = Number(summary.bankInAmount);
  if (Number.isFinite(banked)) return Math.max(0, banked);
  return 0;
}

function BankInSlipChip({
  summary,
  onOpen,
}: {
  summary: BankInSummary;
  onOpen: () => void;
}) {
  const settled = isBankInFullySettled(summary);
  const remaining = remainingAmountOf(summary);
  const statusLabel = settled
    ? `Fully settled: RM ${moneyWithThousands(paidAmountOf(summary))}`
    : `Amount owed: RM ${moneyWithThousands(remaining)}`;
  const verified = Boolean(settled && summary.bankInVerified);
  return (
    <div className="mt-1 inline-flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={onOpen}
        className={`inline-flex flex-col items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
          settled
            ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100"
            : "border-red-200 bg-red-50 text-red-800 hover:bg-red-100"
        }`}
        title={`${statusLabel} — view bank-in receipt details`}
      >
        <span className="inline-flex items-center gap-1">
          <Lucide icon="Receipt" className="w-3 h-3" />
          {summary.receiptCount} Bank In Slip{summary.receiptCount > 1 ? "s" : ""}
        </span>
        <span className="tabular-nums leading-tight">{statusLabel}</span>
      </button>
      {verified ? (
        <Lucide
          icon="BadgeCheck"
          className="w-4 h-4 shrink-0 text-green-500 stroke-[2.25]"
          title="Verified"
          aria-label="Verified"
        />
      ) : null}
    </div>
  );
}

type BankSlip = {
  id: string;
  imageUrl: string;
  s3Key: string;
  createdAt: string;
};
type BankPayment = {
  id: string;
  amount: number;
  remark: string | null;
  createdAt: string;
  supervisorName: string | null;
  bankSlips: BankSlip[];
};
type BankReceiptReason = {
  reason: string;
  createdAt: string;
  supervisorName: string | null;
};
type BankReceiptDetails = {
  teamSalesId: string;
  teamId: string;
  teamName: string | null;
  salesDate: string;
  bankedAt: string;
  bankInAmount: number | null;
  bankInDifference: number;
  remainingAmount?: number;
  paidAmount?: number;
  expectedTotal?: number;
  bankInVerified?: boolean;
  bankInVerifiedAt?: string | null;
  bankInVerifiedByName?: string | null;
  remark?: string | null;
  reasons?: BankReceiptReason[];
  receiptCount: number;
  bankSlips: BankSlip[];
  payments: BankPayment[];
};

function seedReceiptDetails(
  teamId: string,
  salesDate: string,
  teamName: string | null,
  summary: BankInSummary
): BankReceiptDetails {
  return {
    teamSalesId: summary.teamSalesId,
    teamId,
    teamName,
    salesDate,
    bankedAt: summary.bankedAt || "",
    bankInAmount: summary.bankInAmount,
    bankInDifference: summary.bankInDifference,
    remainingAmount: remainingAmountOf(summary),
    paidAmount: paidAmountOf(summary),
    expectedTotal: summary.expectedTotal,
    bankInVerified: Boolean(summary.bankInVerified),
    bankInVerifiedAt: summary.bankInVerifiedAt ?? null,
    bankInVerifiedByName: null,
    remark: null,
    reasons: [],
    receiptCount: summary.receiptCount,
    bankSlips: [],
    payments: [],
  };
}

type Row = {
  teamId?: string | null;
  teamName?: string | null;
  productId: string;
  product: string;
  sku: string | null;
  productType?: string | null;
  price: number;
  opening: number;
  closing: number;
  totalPackSold: number;
  totalValue: number;
  reimbValue: number;
  totalRebate: number;
  totalTradeInQty: number | null;
  totalTradeInValue: number | null;
  byDate: Record<string, { restock: number | null; packSold: number | null; value: number | null; tradeIn: number | null }>;
};
type TeamGroup = { key: string; teamId: string | null; name: string; rows: Row[] };
const PRODUCT_TYPE_ORDER = ["Cigarette", "Device", "Refills", "Accessories"] as const;

const FILTERS_STORAGE_KEY = "teamData.filters";

type StoredFilters = { dateRange?: string; teamId?: string };

/** Reads the last-remembered Date Range / Team filter from localStorage (returns null if unset/invalid). */
function loadStoredFilters(): StoredFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as StoredFilters;
  } catch {
    return null;
  }
}

function saveStoredFilters(filters: StoredFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
}

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function malaysiaTodayYmd() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuala_Lumpur",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function shiftDateKey(ymd: string, days: number) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDateHeading(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatDateTime(v: string | null | undefined) {
  if (!v) return "-";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function money(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function moneyWithThousands(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function renderPack(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : String(v);
}

function renderMoney(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : money(v);
}

function isBlankQty(v: number | null | undefined) {
  return v === null || v === undefined || Number(v) === 0;
}

function renderBlankZeroPack(v: number | null | undefined) {
  return isBlankQty(v) ? "-" : String(v);
}

function renderBlankZeroMoney(v: number | null | undefined) {
  return isBlankQty(v) ? "-" : money(v);
}

function isPackSoldEditable(teamId?: string | null, hasSubmittedSales?: boolean): boolean {
  return Boolean(teamId) && Boolean(hasSubmittedSales);
}

type PackSoldEditTarget = {
  teamId: string;
  teamName: string;
  productId: string;
  productName: string;
  salesDate: string;
  currentQty: number;
};

type BackdatedProduct = {
  id: string;
  name: string;
  sku: string | null;
  type: string;
  openingQty: number;
  allowTradeIn: boolean;
  sellingPrice: number;
};

type BackdatedPreview = {
  exists: boolean;
  existingConfirmed: boolean;
  teamId: string;
  teamName: string;
  salesDate: string;
  laterSalesDates: string[];
  rebuiltStockDates?: string[];
  products: BackdatedProduct[];
  message?: string;
};

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

function sortTypeGroups(groups: Map<string, Row[]>) {
  return Array.from(groups.entries()).sort((a, b) => {
    const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
    const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
}

function collectSortedProductTypes(rows: Row[]): string[] {
  const types = new Set<string>();
  for (const r of rows) {
    types.add((r.productType || "Other").trim() || "Other");
  }
  return sortTypeGroups(new Map(Array.from(types, (typeName) => [typeName, []]))).map(([typeName]) => typeName);
}

function groupedRowsByType(list: Row[]) {
  const groups = new Map<string, Row[]>();
  for (const r of list) {
    const typeName = (r.productType || "Other").trim() || "Other";
    const arr = groups.get(typeName) ?? [];
    arr.push(r);
    groups.set(typeName, arr);
  }
  return sortTypeGroups(groups);
}

function safeSheetName(name: string, used: Set<string>): string {
  const base = name.replace(/[\\/*?:[\]]/g, " ").trim().slice(0, 28) || "Sheet";
  let candidate = base;
  let n = 2;
  while (used.has(candidate)) {
    const suffix = ` ${n}`;
    candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  used.add(candidate);
  return candidate;
}

function cellPackSold(v: number | null | undefined): number | string {
  return v === null || v === undefined ? "" : v;
}

function cellMoney(v: number | null | undefined): number | string {
  return v === null || v === undefined ? "" : v;
}

type TeamSalesTotals = {
  totalsByDate: Record<string, { restock: number; packSold: number; value: number; tradeIn: number }>;
  grandTotals: {
    opening: number;
    closing: number;
    totalPackSold: number;
    totalValue: number;
    reimbValue: number;
    totalRebate: number;
    totalTradeInQty: number;
    totalTradeInValue: number;
  };
};

type SheetBuildResult = {
  aoa: (string | number)[][];
  merges: XLSX.Range[];
};

function productTypeLabel(row: Row): string {
  return (row.productType || "Other").trim() || "Other";
}

function orderRowsByProductType(list: Row[]): Row[] {
  return groupedRowsByType(list).flatMap(([, typeRows]) => typeRows);
}

function sumNullable(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null && b == null) return null;
  return Number(a ?? 0) + Number(b ?? 0);
}

function cloneByDate(byDate: Row["byDate"]): Row["byDate"] {
  const out: Row["byDate"] = {};
  for (const [date, cell] of Object.entries(byDate || {})) {
    out[date] = {
      restock: cell.restock,
      packSold: cell.packSold,
      value: cell.value,
      tradeIn: cell.tradeIn,
    };
  }
  return out;
}

/** Sum the same product across teams into one row (All Teams export). */
function aggregateRowsByProduct(list: Row[]): Row[] {
  const byProduct = new Map<string, Row>();
  for (const r of list) {
    const existing = byProduct.get(r.productId);
    if (!existing) {
      byProduct.set(r.productId, {
        ...r,
        teamId: null,
        teamName: null,
        byDate: cloneByDate(r.byDate),
      });
      continue;
    }
    existing.opening = Number(existing.opening ?? 0) + Number(r.opening ?? 0);
    existing.closing = Number(existing.closing ?? 0) + Number(r.closing ?? 0);
    existing.totalPackSold = Number(existing.totalPackSold ?? 0) + Number(r.totalPackSold ?? 0);
    existing.totalValue = Number(existing.totalValue ?? 0) + Number(r.totalValue ?? 0);
    existing.reimbValue = Number(existing.reimbValue ?? 0) + Number(r.reimbValue ?? 0);
    existing.totalRebate = Number(existing.totalRebate ?? 0) + Number(r.totalRebate ?? 0);
    existing.totalTradeInQty = sumNullable(existing.totalTradeInQty, r.totalTradeInQty);
    existing.totalTradeInValue = sumNullable(existing.totalTradeInValue, r.totalTradeInValue);
    if (!existing.price && r.price) existing.price = r.price;
    const dates = new Set([...Object.keys(existing.byDate || {}), ...Object.keys(r.byDate || {})]);
    for (const d of dates) {
      const a = existing.byDate?.[d];
      const b = r.byDate?.[d];
      existing.byDate[d] = {
        restock: sumNullable(a?.restock, b?.restock),
        packSold: sumNullable(a?.packSold, b?.packSold),
        value: sumNullable(a?.value, b?.value),
        tradeIn: sumNullable(a?.tradeIn, b?.tradeIn),
      };
    }
  }
  return orderRowsByProductType(Array.from(byProduct.values()));
}

function buildTeamSalesSheetAoa(
  teamName: string,
  rows: Row[],
  dateColumns: string[],
  meta: { startDate: string; endDate: string; teamLabel: string; productTypesLabel: string },
  totals: TeamSalesTotals
): SheetBuildResult {
  const aoa: (string | number)[][] = [
    ["Team Data Report"],
    ["Date Range", `${meta.startDate} - ${meta.endDate}`],
    ["Team Filter", meta.teamLabel],
    ["Team", teamName],
    ["Product Types", meta.productTypesLabel],
    [],
  ];

  const leftHeaders = ["Product", "Product Type", "SKU", "Selling Price (RM)", "Opening"];
  const rightHeaders = [
    "Closing",
    "Total Pack Sold",
    "Total Value (RM)",
    "Total Reimbursement Value (RM)",
    "Total Rebate (RM)",
    "Total Trade In (Qty)",
    "Total Trade In (RM)",
  ];
  const dateSubHeaders = ["Restock", "Pack Sold", "Value (RM)", "Trade In"];
  const colsPerDate = dateSubHeaders.length;
  const headerTopRow = aoa.length;
  const headerSubRow = headerTopRow + 1;

  const topRow: (string | number)[] = [...leftHeaders];
  const subRow: (string | number)[] = leftHeaders.map(() => "");
  for (const d of dateColumns) {
    topRow.push(formatDateHeading(d), ...Array(colsPerDate - 1).fill(""));
    subRow.push(...dateSubHeaders);
  }
  topRow.push(...rightHeaders);
  subRow.push(...rightHeaders.map(() => ""));
  aoa.push(topRow, subRow);

  const merges: XLSX.Range[] = [];
  for (let c = 0; c < leftHeaders.length; c++) {
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }
  let dateCol = leftHeaders.length;
  for (let i = 0; i < dateColumns.length; i++) {
    merges.push({
      s: { r: headerTopRow, c: dateCol },
      e: { r: headerTopRow, c: dateCol + colsPerDate - 1 },
    });
    dateCol += colsPerDate;
  }
  const rightStart = leftHeaders.length + dateColumns.length * colsPerDate;
  for (let i = 0; i < rightHeaders.length; i++) {
    const c = rightStart + i;
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }

  for (const r of rows) {
    const row: (string | number)[] = [r.product, productTypeLabel(r), r.sku || "-", Number(r.price || 0), Number(r.opening ?? 0)];
    for (const d of dateColumns) {
      row.push(
        cellPackSold(r.byDate?.[d]?.restock),
        cellPackSold(r.byDate?.[d]?.packSold),
        cellMoney(r.byDate?.[d]?.value),
        cellPackSold(r.byDate?.[d]?.tradeIn)
      );
    }
    row.push(
      Number(r.closing ?? 0),
      Number(r.totalPackSold ?? 0),
      Number(r.totalValue ?? 0),
      Number(r.reimbValue ?? 0),
      Number(r.totalRebate ?? 0),
      cellPackSold(r.totalTradeInQty),
      cellMoney(r.totalTradeInValue)
    );
    aoa.push(row);
  }

  const totalRow: (string | number)[] = ["Total", "-", "-", "", totals.grandTotals.opening];
  for (const d of dateColumns) {
    totalRow.push(
      totals.totalsByDate[d]?.restock ?? 0,
      totals.totalsByDate[d]?.packSold ?? 0,
      totals.totalsByDate[d]?.value ?? 0,
      totals.totalsByDate[d]?.tradeIn ?? 0
    );
  }
  totalRow.push(
    totals.grandTotals.closing,
    totals.grandTotals.totalPackSold,
    totals.grandTotals.totalValue,
    totals.grandTotals.reimbValue,
    totals.grandTotals.totalRebate,
    totals.grandTotals.totalTradeInQty,
    totals.grandTotals.totalTradeInValue
  );
  aoa.push(totalRow);

  return { aoa, merges };
}

function buildTeamStockInOutSheetAoa(
  teamName: string,
  rows: Row[],
  dateColumns: string[],
  meta: { startDate: string; endDate: string; teamLabel: string; productTypesLabel: string },
  totals: TeamSalesTotals
): SheetBuildResult {
  const aoa: (string | number)[][] = [
    ["Team Stock IN/OUT Report"],
    ["Date Range", `${meta.startDate} - ${meta.endDate}`],
    ["Team Filter", meta.teamLabel],
    ["Team", teamName],
    ["Product Types", meta.productTypesLabel],
    [],
  ];

  const leftHeaders = ["Product", "Product Type", "SKU", "Opening"];
  // Trade In is stock leaving the team (same as Stock OUT), so Total Stock OUT includes it.
  const rightHeaders = ["Closing", "Total Stock IN", "Total Stock OUT"];
  const dateSubHeaders = ["Stock IN (Restock)", "Stock OUT (Pack Sold)", "Trade In"];
  const colsPerDate = dateSubHeaders.length;
  const headerTopRow = aoa.length;
  const headerSubRow = headerTopRow + 1;

  const topRow: (string | number)[] = [...leftHeaders];
  const subRow: (string | number)[] = leftHeaders.map(() => "");
  for (const d of dateColumns) {
    topRow.push(formatDateHeading(d), ...Array(colsPerDate - 1).fill(""));
    subRow.push(...dateSubHeaders);
  }
  topRow.push(...rightHeaders);
  subRow.push(...rightHeaders.map(() => ""));
  aoa.push(topRow, subRow);

  const merges: XLSX.Range[] = [];
  for (let c = 0; c < leftHeaders.length; c++) {
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }
  let dateCol = leftHeaders.length;
  for (let i = 0; i < dateColumns.length; i++) {
    merges.push({
      s: { r: headerTopRow, c: dateCol },
      e: { r: headerTopRow, c: dateCol + colsPerDate - 1 },
    });
    dateCol += colsPerDate;
  }
  const rightStart = leftHeaders.length + dateColumns.length * colsPerDate;
  for (let i = 0; i < rightHeaders.length; i++) {
    const c = rightStart + i;
    merges.push({ s: { r: headerTopRow, c }, e: { r: headerSubRow, c } });
  }

  for (const r of rows) {
    const row: (string | number)[] = [r.product, productTypeLabel(r), r.sku || "-", Number(r.opening ?? 0)];
    let totalStockIn = 0;
    let totalStockOut = 0;
    for (const d of dateColumns) {
      const restock = Number(r.byDate?.[d]?.restock ?? 0);
      const packSold = Number(r.byDate?.[d]?.packSold ?? 0);
      const tradeIn = Number(r.byDate?.[d]?.tradeIn ?? 0);
      totalStockIn += restock;
      totalStockOut += packSold + tradeIn;
      row.push(cellPackSold(r.byDate?.[d]?.restock), cellPackSold(r.byDate?.[d]?.packSold), cellPackSold(r.byDate?.[d]?.tradeIn));
    }
    row.push(Number(r.closing ?? 0), totalStockIn, totalStockOut);
    aoa.push(row);
  }

  const totalRow: (string | number)[] = ["Total", "-", "-", totals.grandTotals.opening];
  let grandTotalStockIn = 0;
  let grandTotalStockOut = 0;
  for (const d of dateColumns) {
    const restockSum = totals.totalsByDate[d]?.restock ?? 0;
    const packSoldSum = totals.totalsByDate[d]?.packSold ?? 0;
    const tradeInSum = totals.totalsByDate[d]?.tradeIn ?? 0;
    grandTotalStockIn += restockSum;
    grandTotalStockOut += packSoldSum + tradeInSum;
    totalRow.push(restockSum, packSoldSum, tradeInSum);
  }
  totalRow.push(totals.grandTotals.closing, grandTotalStockIn, grandTotalStockOut);
  aoa.push(totalRow);

  return { aoa, merges };
}

function applySheetBuild(ws: XLSX.WorkSheet, build: SheetBuildResult) {
  if (build.merges.length) {
    ws["!merges"] = [...(ws["!merges"] || []), ...build.merges];
  }
}

/**
 * Renders the (potentially very large, unvirtualized) team/product/date breakdown table.
 * Memoized so that unrelated state changes in the parent (e.g. opening the
 * Export Report / Pack Sold / Bank Receipt dialogs) don't force React to
 * re-render/reconcile this whole tree — that reconciliation work was what
 * made those dialogs feel slow to appear.
 */
const TeamSalesTable = memo(function TeamSalesTable({
  loading,
  teamGroups,
  dates,
  expandedTypeNames,
  onToggleSection,
  bankInByTeamDate,
  buildTotals,
  onOpenReceiptDetails,
  onOpenPackSoldEdit,
}: {
  loading: boolean;
  teamGroups: TeamGroup[];
  dates: string[];
  expandedTypeNames: Set<string>;
  onToggleSection: (sectionKey: string) => void;
  bankInByTeamDate: Record<string, BankInSummary>;
  buildTotals: (list: Row[]) => TeamSalesTotals;
  onOpenReceiptDetails: (teamId: string, salesDate: string) => void;
  onOpenPackSoldEdit: (row: Row, teamName: string, salesDate: string, packSold: number) => void;
}) {
  if (teamGroups.length === 0 || teamGroups.every((g) => g.rows.length === 0)) {
    return (
      <div className="flex flex-col box box--stacked">
        <div className="px-5 py-6 text-slate-500 text-sm">
          {loading ? "Loading..." : "No team sales or stock data found for selected filter."}
        </div>
      </div>
    );
  }

  return (
    <>
      {teamGroups.map((teamGroup) => (
        <div key={teamGroup.key} className="flex flex-col box box--stacked">
          <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
            <div className="font-medium">{teamGroup.name}</div>
          </div>
          {groupedRowsByType(teamGroup.rows).map(([typeName, typeRows]) => {
            const sectionKey = `${teamGroup.key}::${typeName}`;
            const expanded = expandedTypeNames.has(sectionKey);
            const { totalsByDate, grandTotals } = buildTotals(typeRows);
            return (
              <div key={sectionKey}>
                <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 text-left"
                    onClick={() => onToggleSection(sectionKey)}
                  >
                    <Lucide icon={expanded ? "ChevronDown" : "ChevronRight"} className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">{typeName}</span>
                    <span className="text-xs text-slate-500">({typeRows.length})</span>
                  </button>
                </div>
                {expanded ? (
                  <div className="max-w-full overflow-x-auto px-4 pb-4">
                    <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
                      <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                        <Table.Tr>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] pl-3 pr-2 align-middle"
                          >
                            Product
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[220px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] pl-2 pr-2 align-middle"
                          >
                            SKU
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[350px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] text-right pl-2 pr-2 align-middle whitespace-nowrap"
                          >
                            Selling Price
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[445px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] text-right pl-2 pr-2 align-middle whitespace-nowrap"
                          >
                            Opening
                          </Table.Td>
                          {dates.map((d) => {
                            const summaryKey = teamGroup.teamId ? `${teamGroup.teamId}::${d}` : "";
                            const summary = summaryKey ? bankInByTeamDate[summaryKey] : undefined;
                            return (
                              <Table.Td
                                key={d}
                                colSpan={4}
                                className="py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[340px] whitespace-nowrap"
                              >
                                <div>{formatDateHeading(d)}</div>
                                {summary?.hasReceipt && teamGroup.teamId ? (
                                  <BankInSlipChip
                                    summary={summary}
                                    onOpen={() => void onOpenReceiptDetails(teamGroup.teamId!, d)}
                                  />
                                ) : summary?.addedByName ? (
                                  <div
                                    className="mt-1 text-[10px] font-medium text-primary leading-tight"
                                    title={summary.addedByAdmin ? "Added by admin" : "Added by user"}
                                  >
                                    Added by{summary.addedByAdmin ? " admin" : ""}: {summary.addedByName}
                                  </div>
                                ) : (
                                  <div className="mt-1 text-[10px] text-slate-400">No receipt</div>
                                )}
                                {summary?.hasReceipt && summary.addedByName ? (
                                  <div className="mt-0.5 text-[10px] text-slate-500 leading-tight">
                                    Added by{summary.addedByAdmin ? " admin" : ""}: {summary.addedByName}
                                  </div>
                                ) : null}
                              </Table.Td>
                            );
                          })}
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-slate-50 dark:bg-darkmode-700 border-l border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap"
                          >
                            Closing
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-success/20 dark:bg-success/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-normal leading-tight"
                          >
                            Total Pack
                            <br />
                            Sold
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-warning/20 dark:bg-warning/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                          >
                            Total Value
                            <br />
                            (RM)
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[130px] whitespace-normal leading-tight"
                          >
                            Total Reimbursement
                            <br />
                            Value (RM)
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-rose-500/15 dark:bg-rose-500/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                          >
                            Total Rebate
                            <br />
                            (RM)
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-cyan-500/15 dark:bg-cyan-500/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                          >
                            Total Trade In
                            <br />
                            (Qty)
                          </Table.Td>
                          <Table.Td
                            rowSpan={2}
                            className="py-2 px-2 font-medium text-center bg-sky-500/15 dark:bg-sky-500/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                          >
                            Total Trade In
                            <br />
                            (RM)
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          {dates.flatMap((d) => [
                            <Table.Td
                              key={`${d}-restock`}
                              className="py-2 px-2 text-center font-medium bg-primary/10 dark:bg-primary/10 whitespace-nowrap min-w-[85px] w-[85px]"
                            >
                              Restock
                            </Table.Td>,
                            <Table.Td
                              key={`${d}-pack`}
                              className="py-2 px-2 text-center font-medium bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[85px] w-[85px]"
                            >
                              Pack Sold
                            </Table.Td>,
                            <Table.Td
                              key={`${d}-value`}
                              className="py-2 px-2 text-center font-medium bg-warning/10 dark:bg-warning/10 whitespace-nowrap min-w-[85px] w-[85px]"
                            >
                              Value (RM)
                            </Table.Td>,
                            <Table.Td
                              key={`${d}-trade-in`}
                              className="py-2 px-2 text-center font-medium bg-slate-100/80 dark:bg-darkmode-700/40 whitespace-nowrap min-w-[85px] w-[85px]"
                            >
                              Trade In
                            </Table.Td>,
                          ])}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {typeRows.map((r) => (
                          <Table.Tr key={`${r.teamId || "none"}-${r.productId}`} className="[&_td]:border-b [&_td]:border-slate-200/60">
                            <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate pl-3 pr-2">
                              {r.product}
                            </Table.Td>
                            <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] truncate pl-2 pr-2">
                              {r.sku || "-"}
                            </Table.Td>
                            <Table.Td className="py-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-50 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                              {money(r.price)}
                            </Table.Td>
                            <Table.Td className="py-2 text-right tabular-nums sticky left-[445px] z-20 bg-slate-50 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                              {renderPack(r.opening)}
                            </Table.Td>
                            {dates.flatMap((d) => {
                              const packSold = r.byDate?.[d]?.packSold;
                              const hasSubmittedSales = Boolean(
                                teamGroup.teamId && bankInByTeamDate[`${teamGroup.teamId}::${d}`]
                              );
                              const packEditable = isPackSoldEditable(r.teamId, hasSubmittedSales);
                              return [
                                <Table.Td
                                  key={`${r.productId}-${d}-restock`}
                                  className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-primary/10 dark:bg-primary/10 whitespace-nowrap"
                                >
                                  {renderPack(r.byDate?.[d]?.restock)}
                                </Table.Td>,
                                <Table.Td
                                  key={`${r.productId}-${d}-pack`}
                                  className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-success/10 dark:bg-success/10 whitespace-nowrap"
                                >
                                  {packEditable ? (
                                    <button
                                      type="button"
                                      className="w-full rounded px-1 py-0.5 hover:bg-success/30 hover:ring-1 hover:ring-success/40"
                                      title="Edit pack sold"
                                      onClick={() => onOpenPackSoldEdit(r, teamGroup.name, d, packSold ?? 0)}
                                    >
                                      {renderBlankZeroPack(packSold)}
                                    </button>
                                  ) : (
                                    renderBlankZeroPack(packSold)
                                  )}
                                </Table.Td>,
                                <Table.Td
                                  key={`${r.productId}-${d}-value`}
                                  className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-warning/10 dark:bg-warning/10 whitespace-nowrap"
                                >
                                  {renderBlankZeroMoney(r.byDate?.[d]?.value)}
                                </Table.Td>,
                                <Table.Td
                                  key={`${r.productId}-${d}-trade-in`}
                                  className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-slate-100/80 dark:bg-darkmode-700/40 whitespace-nowrap"
                                >
                                  {renderBlankZeroPack(r.byDate?.[d]?.tradeIn)}
                                </Table.Td>,
                              ];
                            })}
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-slate-50/70 dark:bg-darkmode-700/40 border-l border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                              {renderPack(r.closing)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/20 dark:bg-success/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-nowrap">
                              {r.totalPackSold ?? 0}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/20 dark:bg-warning/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(r.totalValue ?? 0)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(r.reimbValue ?? 0)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-rose-500/10 dark:bg-rose-500/10 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(r.totalRebate ?? 0)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-cyan-500/10 dark:bg-cyan-500/10 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {renderPack(r.totalTradeInQty)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-sky-500/10 dark:bg-sky-500/10 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {renderMoney(r.totalTradeInValue)}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                        {typeRows.length > 0 ? (
                          <Table.Tr className="font-medium [&_td]:border-b [&_td]:border-slate-300/80 dark:[&_td]:border-darkmode-400">
                            <Table.Td className="py-2 bg-slate-100 dark:bg-darkmode-700 sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] pl-3 pr-2 whitespace-nowrap">
                              Total
                            </Table.Td>
                            <Table.Td className="py-2 bg-slate-100 dark:bg-darkmode-700 sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] pl-2 pr-2 whitespace-nowrap">
                              -
                            </Table.Td>
                            <Table.Td className="py-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                              -
                            </Table.Td>
                            <Table.Td className="py-2 text-right tabular-nums sticky left-[445px] z-20 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                              {grandTotals.opening}
                            </Table.Td>
                            {dates.flatMap((d) => [
                              <Table.Td
                                key={`total-${d}-restock`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-primary/20 dark:bg-primary/20 whitespace-nowrap"
                              >
                                {totalsByDate[d]?.restock ?? 0}
                              </Table.Td>,
                              <Table.Td
                                key={`total-${d}-pack`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-success/20 dark:bg-success/20 whitespace-nowrap"
                              >
                                {totalsByDate[d]?.packSold ?? 0}
                              </Table.Td>,
                              <Table.Td
                                key={`total-${d}-value`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-warning/20 dark:bg-warning/20 whitespace-nowrap"
                              >
                                {moneyWithThousands(totalsByDate[d]?.value ?? 0)}
                              </Table.Td>,
                              <Table.Td
                                key={`total-${d}-trade-in`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-slate-200/70 dark:bg-darkmode-700 whitespace-nowrap"
                              >
                                {totalsByDate[d]?.tradeIn ?? 0}
                              </Table.Td>,
                            ])}
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-slate-200/70 dark:bg-darkmode-700 border-l border-slate-200 dark:border-darkmode-400 min-w-[95px] whitespace-nowrap">
                              {grandTotals.closing}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/30 dark:bg-success/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-nowrap">
                              {grandTotals.totalPackSold}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(grandTotals.totalValue)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/40 dark:bg-warning/40 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(grandTotals.reimbValue)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-rose-500/25 dark:bg-rose-500/25 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(grandTotals.totalRebate)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-cyan-500/25 dark:bg-cyan-500/25 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {grandTotals.totalTradeInQty}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-sky-500/25 dark:bg-sky-500/25 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(grandTotals.totalTradeInValue)}
                            </Table.Td>
                          </Table.Tr>
                        ) : null}
                      </Table.Tbody>
                    </Table>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
});

export default function TeamSalesDataPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);
  const defaultDateRange = `${defaultStart} - ${defaultEnd}`;

  const [dateRange, setDateRange] = useState(() => {
    const stored = loadStoredFilters();
    const storedRange = stored?.dateRange?.trim();
    return storedRange && parseDateRangeInput(storedRange) ? storedRange : defaultDateRange;
  });
  const [teamId, setTeamId] = useState(() => loadStoredFilters()?.teamId?.trim() || "");

  const [teams, setTeams] = useState<Team[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [bankInByTeamDate, setBankInByTeamDate] = useState<Record<string, BankInSummary>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expandedTypeNames, setExpandedTypeNames] = useState<Set<string>>(new Set());
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [receiptDetails, setReceiptDetails] = useState<BankReceiptDetails | null>(null);
  const [receiptVerifying, setReceiptVerifying] = useState(false);
  const receiptCacheRef = useRef<Record<string, BankReceiptDetails>>({});
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportSelectedTypes, setExportSelectedTypes] = useState<Set<string>>(new Set());
  const [exportReportKinds, setExportReportKinds] = useState<Set<"SALES" | "STOCK">>(new Set(["SALES"]));
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [packSoldEdit, setPackSoldEdit] = useState<PackSoldEditTarget | null>(null);
  const [packSoldEditQty, setPackSoldEditQty] = useState("");
  const [packSoldEditReason, setPackSoldEditReason] = useState("");
  const [packSoldEditError, setPackSoldEditError] = useState("");
  const [packSoldSaving, setPackSoldSaving] = useState(false);
  const [backdatedOpen, setBackdatedOpen] = useState(false);
  const [backdatedTeamId, setBackdatedTeamId] = useState("");
  const [backdatedDate, setBackdatedDate] = useState("");
  const [backdatedPreview, setBackdatedPreview] = useState<BackdatedPreview | null>(null);
  const [backdatedLoading, setBackdatedLoading] = useState(false);
  const [backdatedSaving, setBackdatedSaving] = useState(false);
  const [backdatedError, setBackdatedError] = useState("");
  const [backdatedReason, setBackdatedReason] = useState("");
  const [backdatedQtys, setBackdatedQtys] = useState<Record<string, { qtySold: string; tradeInQty: string }>>({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/teams?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list: Team[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setTeams(list.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        // keep page usable without teams list
      }
    })();
  }, [apiUrl, token]);

  const loadData = async (opts?: { preserveSuccess?: boolean; silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError("");
    if (!opts?.preserveSuccess) setSuccess("");
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      if (teamId) params.set("teamId", teamId);
      params.set("_", String(Date.now()));

      const res = await axios.get(`${apiUrl}/team-sales-data?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDates(Array.isArray(res.data?.dates) ? res.data.dates : []);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setBankInByTeamDate(res.data?.bankInByTeamDate && typeof res.data.bankInByTeamDate === "object" ? res.data.bankInByTeamDate : {});
      receiptCacheRef.current = {};
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load Team Data.");
      setDates([]);
      setRows([]);
      setBankInByTeamDate({});
      receiptCacheRef.current = {};
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  // Remember the last-used Date Range / Team filter so it's restored on the next visit.
  useEffect(() => {
    saveStoredFilters({ dateRange, teamId });
  }, [dateRange, teamId]);

  const isAllTeams = !teamId;
  const colCount = useMemo(() => 5 + dates.length * 4 + 4, [dates.length]);

  const teamGroups = useMemo<TeamGroup[]>(() => {
    if (!isAllTeams) {
      const selected = teams.find((t) => t.id === teamId);
      return [{ key: teamId || "selected", teamId: teamId || rows[0]?.teamId || null, name: selected?.name || "Selected Team", rows }];
    }

    const byTeam = new Map<string, TeamGroup>();
    for (const r of rows) {
      const key = r.teamId || "unknown";
      const name = r.teamName || "Unknown Team";
      const existing = byTeam.get(key);
      if (existing) {
        existing.rows.push(r);
      } else {
        byTeam.set(key, { key, teamId: r.teamId || null, name, rows: [r] });
      }
    }

    return Array.from(byTeam.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [isAllTeams, rows, teamId, teams]);

  const openReceiptDetails = useCallback(
    async (teamIdValue: string, salesDate: string) => {
      const summaryKey = `${teamIdValue}::${salesDate}`;
      const cached = receiptCacheRef.current[summaryKey];
      const summary = bankInByTeamDate[summaryKey];
      const teamName =
        teams.find((t) => t.id === teamIdValue)?.name ||
        teamGroups.find((g) => g.teamId === teamIdValue)?.name ||
        null;

      flushSync(() => {
        setReceiptModalOpen(true);
        setReceiptError("");
        setReceiptVerifying(false);
        if (cached) {
          setReceiptDetails(cached);
          setReceiptLoading(false);
        } else if (summary) {
          setReceiptDetails(seedReceiptDetails(teamIdValue, salesDate, teamName, summary));
          setReceiptLoading(true);
        } else {
          setReceiptDetails(null);
          setReceiptLoading(true);
        }
      });

      if (cached) return;

      try {
        const params = new URLSearchParams();
        params.set("teamId", teamIdValue);
        params.set("salesDate", salesDate);
        const res = await axios.get(`${apiUrl}/team-sales-data/bank-receipts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (res.data?.data || null) as BankReceiptDetails | null;
        if (data) {
          receiptCacheRef.current[summaryKey] = data;
          setReceiptDetails(data);
        }
      } catch (e: any) {
        setReceiptError(e?.response?.data?.message || "Failed to load bank receipt details.");
      } finally {
        setReceiptLoading(false);
      }
    },
    [apiUrl, token, bankInByTeamDate, teams, teamGroups]
  );

  const verifyReceiptBankIn = async () => {
    if (!receiptDetails) return;
    const remaining = Number(receiptDetails.remainingAmount);
    const remainingValue = Number.isFinite(remaining)
      ? remaining
      : Math.max(0, -Number(receiptDetails.bankInDifference || 0));
    if (remainingValue > SETTLED_REMAINING_EPS) return;
    if (receiptDetails.bankInVerified) return;

    setReceiptVerifying(true);
    setReceiptError("");
    try {
      const res = await axios.post(
        `${apiUrl}/team-sales-data/verify-bank-in`,
        { teamId: receiptDetails.teamId, salesDate: receiptDetails.salesDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const verifiedAt = (res.data?.data?.bankInVerifiedAt as string | undefined) || new Date().toISOString();
      const verifiedByName = (res.data?.data?.bankInVerifiedByName as string | null | undefined) ?? null;
      setReceiptDetails((prev) => {
        const next = prev
          ? {
              ...prev,
              bankInVerified: true,
              bankInVerifiedAt: verifiedAt,
              bankInVerifiedByName: verifiedByName,
            }
          : prev;
        if (next) receiptCacheRef.current[`${next.teamId}::${next.salesDate}`] = next;
        return next;
      });
      const summaryKey = `${receiptDetails.teamId}::${receiptDetails.salesDate}`;
      setBankInByTeamDate((prev) => {
        const existing = prev[summaryKey];
        if (!existing) return prev;
        return {
          ...prev,
          [summaryKey]: {
            ...existing,
            bankInVerified: true,
            bankInVerifiedAt: verifiedAt,
          },
        };
      });
      setSuccess("Bank in slip verified.");
    } catch (e: any) {
      setReceiptError(e?.response?.data?.message || "Failed to verify bank in slip.");
    } finally {
      setReceiptVerifying(false);
    }
  };

  const toggleSection = useCallback((sectionKey: string) => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) next.delete(sectionKey);
      else next.add(sectionKey);
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      for (const team of teamGroups) {
        const grouped = groupedRowsByType(team.rows);
        for (const [typeName] of grouped) {
          next.add(`${team.key}::${typeName}`);
        }
      }
      return next;
    });
  }, [teamGroups]);

  const buildTotals = useCallback(
    (list: Row[]): TeamSalesTotals => {
      const totalsByDate: Record<string, { restock: number; packSold: number; value: number; tradeIn: number }> = {};
      for (const d of dates) totalsByDate[d] = { restock: 0, packSold: 0, value: 0, tradeIn: 0 };
      for (const r of list) {
        for (const d of dates) {
          totalsByDate[d].restock += Number(r.byDate?.[d]?.restock ?? 0);
          totalsByDate[d].packSold += Number(r.byDate?.[d]?.packSold ?? 0);
          totalsByDate[d].value += Number(r.byDate?.[d]?.value ?? 0);
          totalsByDate[d].tradeIn += Number(r.byDate?.[d]?.tradeIn ?? 0);
        }
      }
      const grandTotals = list.reduce(
        (acc, r) => {
          acc.opening += Number(r.opening ?? 0);
          acc.closing += Number(r.closing ?? 0);
          acc.totalPackSold += Number(r.totalPackSold ?? 0);
          acc.totalValue += Number(r.totalValue ?? 0);
          acc.reimbValue += Number(r.reimbValue ?? 0);
          acc.totalRebate += Number(r.totalRebate ?? 0);
          acc.totalTradeInQty += Number(r.totalTradeInQty ?? 0);
          acc.totalTradeInValue += Number(r.totalTradeInValue ?? 0);
          return acc;
        },
        {
          opening: 0,
          closing: 0,
          totalPackSold: 0,
          totalValue: 0,
          reimbValue: 0,
          totalRebate: 0,
          totalTradeInQty: 0,
          totalTradeInValue: 0,
        }
      );
      return { totalsByDate, grandTotals };
    },
    [dates]
  );

  const availableExportTypes = useMemo(() => collectSortedProductTypes(rows), [rows]);

  const productCountByType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const typeName = (r.productType || "Other").trim() || "Other";
      counts.set(typeName, (counts.get(typeName) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const teamLabel = useMemo(() => {
    if (!teamId) return "All Teams";
    return teams.find((t) => t.id === teamId)?.name || "All Teams";
  }, [teamId, teams]);

  const openExportModal = () => {
    if (rows.length === 0) {
      setError("No data to export. Apply filter first.");
      return;
    }
    setExportError("");
    setExportSelectedTypes(new Set(availableExportTypes));
    setExportReportKinds(new Set(["SALES"]));
    setExportModalOpen(true);
  };

  const handleExportReport = () => {
    if (exportReportKinds.size === 0) {
      setExportError("Select at least one report type (Sales Report and/or Stock IN/OUT Report).");
      return;
    }
    if (exportSelectedTypes.size === 0) {
      setExportError("Select at least one product type.");
      return;
    }
    const parsedRange = parseDateRangeInput(dateRange);
    if (!parsedRange) {
      setExportError("Please select a valid date range.");
      return;
    }

    const sheetsToExport: { sheetName: string; teamName: string; rows: Row[] }[] = [];
    if (isAllTeams) {
      const filteredRows = rows.filter((r) => exportSelectedTypes.has(productTypeLabel(r)));
      const aggregatedRows = aggregateRowsByProduct(filteredRows);
      if (aggregatedRows.length > 0) {
        sheetsToExport.push({
          sheetName: "All Teams",
          teamName: "All Teams",
          rows: aggregatedRows,
        });
      }
    } else {
      for (const teamGroup of teamGroups) {
        const filteredRows = teamGroup.rows.filter((r) => exportSelectedTypes.has(productTypeLabel(r)));
        if (filteredRows.length === 0) continue;
        sheetsToExport.push({
          sheetName: teamGroup.name,
          teamName: teamGroup.name,
          rows: orderRowsByProductType(filteredRows),
        });
      }
    }

    if (sheetsToExport.length === 0) {
      setExportError("No data for selected product types.");
      return;
    }

    setExporting(true);
    setExportError("");
    try {
      const productTypesLabel = availableExportTypes.filter((typeName) => exportSelectedTypes.has(typeName)).join(", ");
      const meta = {
        startDate: parsedRange.startDate,
        endDate: parsedRange.endDate,
        teamLabel,
        productTypesLabel,
      };
      const teamSlug = teamLabel.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "all-teams";

      if (exportReportKinds.has("SALES")) {
        const wb = XLSX.utils.book_new();
        const usedSheetNames = new Set<string>();
        for (const sheet of sheetsToExport) {
          const build = buildTeamSalesSheetAoa(
            sheet.teamName,
            sheet.rows,
            dates,
            meta,
            buildTotals(sheet.rows)
          );
          const ws = XLSX.utils.aoa_to_sheet(build.aoa);
          applySheetBuild(ws, build);
          XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheet.sheetName, usedSheetNames));
        }
        const filename = `team-sales-${parsedRange.startDate}-to-${parsedRange.endDate}-${teamSlug}.xlsx`;
        XLSX.writeFile(wb, filename);
      }

      if (exportReportKinds.has("STOCK")) {
        const wb = XLSX.utils.book_new();
        const usedSheetNames = new Set<string>();
        for (const sheet of sheetsToExport) {
          const build = buildTeamStockInOutSheetAoa(
            sheet.teamName,
            sheet.rows,
            dates,
            meta,
            buildTotals(sheet.rows)
          );
          const ws = XLSX.utils.aoa_to_sheet(build.aoa);
          applySheetBuild(ws, build);
          XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sheet.sheetName, usedSheetNames));
        }
        const filename = `team-stock-${parsedRange.startDate}-to-${parsedRange.endDate}-${teamSlug}.xlsx`;
        XLSX.writeFile(wb, filename);
      }

      setExportModalOpen(false);
    } catch {
      setExportError("Failed to export report.");
    } finally {
      setExporting(false);
    }
  };

  const openPackSoldEdit = useCallback(
    (row: Row, teamName: string, salesDate: string, packSold: number) => {
      if (!row.teamId) return;
      flushSync(() => {
        setPackSoldSaving(false);
        setPackSoldEditError("");
        setPackSoldEdit({
          teamId: row.teamId,
          teamName,
          productId: row.productId,
          productName: row.product,
          salesDate,
          currentQty: packSold,
        });
        setPackSoldEditQty(String(packSold));
        setPackSoldEditReason("");
      });
    },
    []
  );

  const closePackSoldEdit = () => {
    if (packSoldSaving) return;
    setPackSoldEdit(null);
    setPackSoldEditQty("");
    setPackSoldEditReason("");
    setPackSoldEditError("");
  };

  const savePackSoldEdit = async () => {
    if (!packSoldEdit) return;
    const qty = Math.floor(Number(packSoldEditQty));
    if (!Number.isFinite(qty) || qty < 0) {
      setPackSoldEditError("Pack sold must be a whole number ≥ 0.");
      return;
    }
    const reason = packSoldEditReason.trim();
    if (!reason) {
      setPackSoldEditError("Reason is required.");
      return;
    }

    setPackSoldSaving(true);
    setPackSoldEditError("");
    setError("");
    setSuccess("");
    try {
      await axios.patch(
        `${apiUrl}/team-sales-data/pack-sold`,
        {
          teamId: packSoldEdit.teamId,
          salesDate: packSoldEdit.salesDate,
          productId: packSoldEdit.productId,
          qtySold: qty,
          reason,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const salesDate = packSoldEdit.salesDate;
      const editedTeamId = packSoldEdit.teamId;
      const editedProductId = packSoldEdit.productId;
      setRows((prev) =>
        prev.map((r) => {
          if (r.teamId !== editedTeamId || r.productId !== editedProductId) return r;
          const prevSold = Number(r.byDate?.[salesDate]?.packSold ?? 0);
          const delta = qty - prevSold;
          const nextValue = Number((qty * Number(r.price || 0)).toFixed(2));
          const prevValue = Number(r.byDate?.[salesDate]?.value ?? 0);
          return {
            ...r,
            closing: r.closing - delta,
            totalPackSold: r.totalPackSold + delta,
            totalValue: Number((r.totalValue + (nextValue - prevValue)).toFixed(2)),
            byDate: {
              ...r.byDate,
              [salesDate]: {
                restock: r.byDate?.[salesDate]?.restock ?? null,
                packSold: qty,
                value: nextValue,
                tradeIn: r.byDate?.[salesDate]?.tradeIn ?? null,
              },
            },
          };
        })
      );
      setPackSoldEdit(null);
      setPackSoldEditQty("");
      setPackSoldEditReason("");
      setPackSoldEditError("");
      setPackSoldSaving(false);
      setSuccess("Pack sold updated. Stock, later days, rebates, and totals have been recalculated.");
      void loadData({ preserveSuccess: true, silent: true });
    } catch (e: any) {
      const data = e?.response?.data;
      const reason =
        (typeof data?.message === "string" && data.message.trim()) ||
        (typeof data?.error === "string" && data.error.trim()) ||
        "";
      setPackSoldEditError(reason || "Failed to update pack sold.");
    } finally {
      setPackSoldSaving(false);
    }
  };

  const yesterdayKey = shiftDateKey(malaysiaTodayYmd(), -1);

  const openBackdatedSales = () => {
    setBackdatedError("");
    setBackdatedReason("");
    setBackdatedQtys({});
    setBackdatedPreview(null);
    setBackdatedTeamId(teamId || teams[0]?.id || "");
    setBackdatedDate(yesterdayKey);
    setBackdatedOpen(true);
  };

  const closeBackdatedSales = () => {
    if (backdatedSaving) return;
    setBackdatedOpen(false);
    setBackdatedError("");
    setBackdatedPreview(null);
    setBackdatedQtys({});
    setBackdatedReason("");
  };

  useEffect(() => {
    if (!backdatedOpen || !backdatedTeamId || !backdatedDate) return;
    let cancelled = false;
    const loadPreview = async () => {
      setBackdatedLoading(true);
      setBackdatedError("");
      try {
        const params = new URLSearchParams();
        params.set("teamId", backdatedTeamId);
        params.set("salesDate", backdatedDate);
        const res = await axios.get(`${apiUrl}/team-sales-data/backdated-sales/preview?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const data = res.data as BackdatedPreview;
        setBackdatedPreview(data);
        setBackdatedQtys({});
        if (data.exists && data.message) setBackdatedError(data.message);
      } catch (e: any) {
        if (cancelled) return;
        setBackdatedPreview(null);
        setBackdatedError(e?.response?.data?.message || "Failed to load products and opening stock.");
      } finally {
        if (!cancelled) setBackdatedLoading(false);
      }
    };
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, token, backdatedOpen, backdatedTeamId, backdatedDate]);

  const backdatedExpectedTotal = useMemo(() => {
    if (!backdatedPreview?.products?.length) return 0;
    return backdatedPreview.products.reduce((sum, p) => {
      const qty = Math.floor(Number(backdatedQtys[p.id]?.qtySold) || 0);
      return sum + qty * Number(p.sellingPrice || 0);
    }, 0);
  }, [backdatedPreview, backdatedQtys]);

  const backdatedProductGroups = useMemo(() => {
    const groups = new Map<string, BackdatedProduct[]>();
    for (const p of backdatedPreview?.products || []) {
      const key = p.type || "Other";
      const arr = groups.get(key) ?? [];
      arr.push(p);
      groups.set(key, arr);
    }
    return Array.from(groups.entries()).sort((a, b) => {
      const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
      const ao = ai === -1 ? PRODUCT_TYPE_ORDER.length : ai;
      const bo = bi === -1 ? PRODUCT_TYPE_ORDER.length : bi;
      return ao - bo;
    });
  }, [backdatedPreview]);

  const saveBackdatedSales = async () => {
    if (!backdatedTeamId || !backdatedDate) {
      setBackdatedError("Select a team and a past sales date.");
      return;
    }
    if (backdatedPreview?.exists) {
      setBackdatedError(backdatedPreview.message || "Sales already exist for this team and date.");
      return;
    }
    const reason = backdatedReason.trim();
    if (!reason) {
      setBackdatedError("Reason is required.");
      return;
    }
    const lines = Object.entries(backdatedQtys)
      .map(([productId, q]) => ({
        productId,
        qtySold: Math.floor(Number(q.qtySold) || 0),
        tradeInQty: Math.floor(Number(q.tradeInQty) || 0),
      }))
      .filter((line) => line.qtySold > 0 || line.tradeInQty > 0);
    if (lines.length === 0) {
      setBackdatedError("Enter at least one product with qty sold or trade-in greater than 0.");
      return;
    }
    for (const line of lines) {
      const product = backdatedPreview?.products.find((p) => p.id === line.productId);
      if (!product) continue;
      if (line.qtySold + line.tradeInQty > product.openingQty) {
        setBackdatedError(
          `${product.name}: Qty Sold + Trade-In exceeds opening stock (${product.openingQty}).`
        );
        return;
      }
    }

    setBackdatedSaving(true);
    setBackdatedError("");
    setError("");
    setSuccess("");
    const savedDate = backdatedDate;
    const teamName =
      backdatedPreview?.teamName ||
      teams.find((t) => t.id === backdatedTeamId)?.name ||
      "team";
    try {
      await axios.post(
        `${apiUrl}/team-sales-data/backdated-sales`,
        {
          teamId: backdatedTeamId,
          salesDate: backdatedDate,
          reason,
          bankInAmount: 0,
          lines,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackdatedOpen(false);
      setBackdatedPreview(null);
      setBackdatedQtys({});
      setBackdatedReason("");
      await loadData();
      setSuccess(`Backdated sales of ${formatDateHeading(savedDate)} for ${teamName} successfully added`);
    } catch (e: any) {
      setBackdatedError(e?.response?.data?.message || e?.message || "Failed to create backdated sales.");
    } finally {
      setBackdatedSaving(false);
    }
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Team Data</div>
              {loading ? <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500 md:ml-3" /> : null}
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:flex-row sm:items-end gap-3 w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_auto_auto] gap-3 w-full sm:w-auto">
                    <div className="sm:w-[420px]">
                      <div className="text-xs text-slate-500 mb-1">Date Range</div>
                      <div className="relative inline-flex w-full">
                        <Lucide
                          icon="Calendar"
                          className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                        />
                        <Litepicker
                          value={dateRange}
                          onChange={(e) => setDateRange((e.target?.value || "").trim())}
                          options={{
                            autoApply: true,
                            singleMode: false,
                            numberOfColumns: 2,
                            numberOfMonths: 2,
                            showWeekNumbers: true,
                            dropdowns: {
                              minYear: 2020,
                              maxYear: null,
                              months: true,
                              years: true,
                            },
                            format: "YYYY-MM-DD",
                          }}
                          className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:w-[170px]">
                      <div className="text-xs text-slate-500 mb-1">Team</div>
                      <FormSelect value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                        <option value="">All Teams</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                    <div className="sm:self-end">
                      <Button variant="primary" onClick={() => void loadData()} disabled={loading} className="whitespace-nowrap px-4">
                        Apply Filter
                      </Button>
                    </div>
                    <div className="sm:self-end">
                      <Button
                        variant="outline-secondary"
                        onClick={openExportModal}
                        disabled={loading || rows.length === 0}
                        className="whitespace-nowrap px-4"
                      >
                        <Lucide icon="Download" className="w-4 h-4 mr-2" />
                        Export Report
                      </Button>
                    </div>
                  </div>
                  <div className="sm:ml-auto sm:self-end">
                    <Button
                      variant="outline-primary"
                      onClick={openBackdatedSales}
                      disabled={loading || teams.length === 0}
                      className="whitespace-nowrap px-4"
                    >
                      <Lucide icon="Plus" className="w-4 h-4 mr-2" />
                      Add Backdated Sales
                    </Button>
                  </div>
                </div>

                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}
                {success ? <div className="px-5 pb-3 text-success text-sm">{success}</div> : null}

              </div>

              <TeamSalesTable
                loading={loading}
                teamGroups={teamGroups}
                dates={dates}
                expandedTypeNames={expandedTypeNames}
                onToggleSection={toggleSection}
                bankInByTeamDate={bankInByTeamDate}
                buildTotals={buildTotals}
                onOpenReceiptDetails={openReceiptDetails}
                onOpenPackSoldEdit={openPackSoldEdit}
              />
            </div>
          </div>
        </div>
        <Dialog open={Boolean(packSoldEdit)} onClose={closePackSoldEdit}>
          <Dialog.Panel className="max-w-md">
            <Dialog.Title>
              <div className="font-medium">Edit Pack Sold</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-4">
              {packSoldEdit ? (
                <>
                  <div className="text-sm space-y-1">
                    <div><span className="text-slate-500">Team:</span> {packSoldEdit.teamName}</div>
                    <div><span className="text-slate-500">Product:</span> {packSoldEdit.productName}</div>
                    <div><span className="text-slate-500">Date:</span> {formatDateHeading(packSoldEdit.salesDate)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Pack Sold</div>
                    <FormInput
                      type="number"
                      min={0}
                      step={1}
                      value={packSoldEditQty}
                      onChange={(e) => setPackSoldEditQty(e.target.value)}
                      disabled={packSoldSaving}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Reason (required)</div>
                    <FormTextarea
                      rows={3}
                      value={packSoldEditReason}
                      onChange={(e) => setPackSoldEditReason(e.target.value)}
                      placeholder="Why is this pack sold being adjusted?"
                      disabled={packSoldSaving}
                    />
                  </div>
                  <div className="text-xs text-slate-500">
                    Value (RM), totals, Closing, and later days will recalculate after save. Days with no submitted sales cannot be edited here — use Add Backdated Sales.
                  </div>
                </>
              ) : null}
              {packSoldEditError ? <div className="text-sm text-danger">{packSoldEditError}</div> : null}
            </Dialog.Description>
            <Dialog.Footer>
              <Button variant="outline-secondary" onClick={closePackSoldEdit} disabled={packSoldSaving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void savePackSoldEdit()} disabled={packSoldSaving || !packSoldEdit}>
                {packSoldSaving ? (
                  <>
                    <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
        <Dialog
          open={backdatedOpen}
          onClose={closeBackdatedSales}
          size="xl"
          staticBackdrop
        >
          <Dialog.Panel>
            <Dialog.Title>
              <div className="font-medium">Add Backdated Sales</div>
            </Dialog.Title>
            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Team</div>
                  <FormSelect
                    value={backdatedTeamId}
                    onChange={(e) => setBackdatedTeamId(e.target.value)}
                    disabled={backdatedSaving}
                  >
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </FormSelect>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Sales Date</div>
                  <div className="relative">
                    <Lucide
                      icon="Calendar"
                      className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500 pointer-events-none"
                    />
                    <FormInput
                      type="date"
                      value={backdatedDate}
                      max={yesterdayKey}
                      onChange={(e) => setBackdatedDate((e.target.value || "").trim().slice(0, 10))}
                      disabled={backdatedSaving}
                      className="w-full pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                    />
                  </div>
                </div>
              </div>
              {backdatedPreview && !backdatedPreview.exists && (backdatedPreview.laterSalesDates?.length ?? 0) > 0 ? (
                <div className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2">
                  Later submitted days will also be rebuilt:{" "}
                  {(backdatedPreview.rebuiltStockDates || [backdatedDate, ...backdatedPreview.laterSalesDates])
                    .map((d) => formatDateHeading(d))
                    .join(", ")}
                  .
                </div>
              ) : null}
              {backdatedLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                  Loading opening stock...
                </div>
              ) : null}
              {!backdatedLoading && backdatedPreview && !backdatedPreview.exists && backdatedPreview.products.length === 0 ? (
                <div className="text-sm text-slate-500">No opening stock for this team on the selected date.</div>
              ) : null}
              {!backdatedLoading && backdatedPreview && !backdatedPreview.exists && backdatedPreview.products.length > 0 ? (
                <div className="max-h-[380px] overflow-auto border border-slate-200/80 rounded-md">
                  <Table className="w-full">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th className="py-2 px-2 text-left text-xs">Product</Table.Th>
                        <Table.Th className="py-2 px-2 text-center text-xs">Opening</Table.Th>
                        <Table.Th className="py-2 px-2 text-center text-xs">Qty Sold</Table.Th>
                        <Table.Th className="py-2 px-2 text-center text-xs">Trade-In</Table.Th>
                        <Table.Th className="py-2 px-2 text-right text-xs">Value (RM)</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {backdatedProductGroups.map(([typeName, typeProducts]) => (
                        <Fragment key={typeName}>
                          <Table.Tr>
                            <Table.Td colSpan={5} className="py-1.5 px-2 text-xs font-semibold bg-slate-50">
                              {typeName}
                            </Table.Td>
                          </Table.Tr>
                          {typeProducts.map((p) => {
                            const qtySold = Math.floor(Number(backdatedQtys[p.id]?.qtySold) || 0);
                            const tradeInQty = Math.floor(Number(backdatedQtys[p.id]?.tradeInQty) || 0);
                            const remaining = p.openingQty - qtySold - tradeInQty;
                            return (
                              <Table.Tr key={p.id}>
                                <Table.Td className="py-1.5 px-2 text-sm">
                                  <div>{p.name}</div>
                                  {p.sku ? <div className="text-[11px] text-slate-400">{p.sku}</div> : null}
                                </Table.Td>
                                <Table.Td className="py-1.5 px-2 text-center tabular-nums text-sm">{p.openingQty}</Table.Td>
                                <Table.Td className="py-1.5 px-2">
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={backdatedQtys[p.id]?.qtySold ?? ""}
                                    onChange={(e) =>
                                      setBackdatedQtys((prev) => ({
                                        ...prev,
                                        [p.id]: {
                                          qtySold: e.target.value,
                                          tradeInQty: prev[p.id]?.tradeInQty ?? "",
                                        },
                                      }))
                                    }
                                    disabled={backdatedSaving}
                                    className="w-[84px] mx-auto block text-center rounded-md border border-slate-300/80 px-2 py-1 text-sm"
                                  />
                                </Table.Td>
                                <Table.Td className="py-1.5 px-2">
                                  {p.allowTradeIn ? (
                                    <input
                                      type="number"
                                      min={0}
                                      step={1}
                                      value={backdatedQtys[p.id]?.tradeInQty ?? ""}
                                      onChange={(e) =>
                                        setBackdatedQtys((prev) => ({
                                          ...prev,
                                          [p.id]: {
                                            qtySold: prev[p.id]?.qtySold ?? "",
                                            tradeInQty: e.target.value,
                                          },
                                        }))
                                      }
                                      disabled={backdatedSaving}
                                      className="w-[84px] mx-auto block text-center rounded-md border border-slate-300/80 px-2 py-1 text-sm"
                                    />
                                  ) : (
                                    <div className="text-center text-slate-400 text-sm">—</div>
                                  )}
                                </Table.Td>
                                <Table.Td className={`py-1.5 px-2 text-right tabular-nums text-sm ${remaining < 0 ? "text-danger" : ""}`}>
                                  {moneyWithThousands(qtySold * Number(p.sellingPrice || 0))}
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Fragment>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              ) : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Total Sales (RM)</div>
                  <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold tabular-nums">
                    {moneyWithThousands(backdatedExpectedTotal)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Reason (required)</div>
                  <FormTextarea
                    rows={3}
                    value={backdatedReason}
                    onChange={(e) => setBackdatedReason(e.target.value)}
                    placeholder="Why is this past date being keyed in now?"
                    disabled={backdatedSaving || Boolean(backdatedPreview?.exists)}
                  />
                </div>
              </div>
              {backdatedError ? <div className="text-sm text-danger">{backdatedError}</div> : null}
            </div>
            <Dialog.Footer>
              <Button variant="outline-secondary" onClick={closeBackdatedSales} disabled={backdatedSaving}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => void saveBackdatedSales()}
                disabled={
                  backdatedSaving ||
                  backdatedLoading ||
                  Boolean(backdatedPreview?.exists) ||
                  !backdatedTeamId ||
                  !backdatedDate
                }
              >
                {backdatedSaving ? (
                  <>
                    <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Create sales & rebuild stock"
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
        <Dialog
          open={exportModalOpen}
          onClose={() => {
            if (exporting) return;
            setExportModalOpen(false);
            setExportError("");
          }}
        >
          <Dialog.Panel className="max-w-md">
            <Dialog.Title>
              <div className="font-medium">Export Report</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-4">
              <div className="text-sm text-slate-600">
                Export the current filter result to Excel. All selected product categories go on one sheet. All Teams sums each product across teams into a single row. Select one or more product types to include.
              </div>
              <div className="space-y-2">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">Report Type</div>
                <div className="border border-slate-200/80 rounded-md divide-y divide-slate-200/60">
                  <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={exportReportKinds.has("SALES")}
                      disabled={exporting}
                      onChange={() => {
                        setExportReportKinds((prev) => {
                          const next = new Set(prev);
                          if (next.has("SALES")) next.delete("SALES");
                          else next.add("SALES");
                          return next;
                        });
                      }}
                    />
                    <span className="text-sm flex-1">Sales Report</span>
                  </label>
                  <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={exportReportKinds.has("STOCK")}
                      disabled={exporting}
                      onChange={() => {
                        setExportReportKinds((prev) => {
                          const next = new Set(prev);
                          if (next.has("STOCK")) next.delete("STOCK");
                          else next.add("STOCK");
                          return next;
                        });
                      }}
                    />
                    <span className="text-sm flex-1">Stock IN/OUT Report</span>
                  </label>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline-secondary"
                  className="text-xs px-2 py-1"
                  onClick={() => setExportSelectedTypes(new Set(availableExportTypes))}
                  disabled={exporting}
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  className="text-xs px-2 py-1"
                  onClick={() => setExportSelectedTypes(new Set())}
                  disabled={exporting}
                >
                  Clear All
                </Button>
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200/80 rounded-md divide-y divide-slate-200/60">
                {availableExportTypes.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-slate-500">No product types in current result.</div>
                ) : (
                  availableExportTypes.map((typeName) => {
                    const count = productCountByType.get(typeName) ?? 0;
                    const checked = exportSelectedTypes.has(typeName);
                    return (
                      <label
                        key={typeName}
                        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={checked}
                          disabled={exporting}
                          onChange={() => {
                            setExportSelectedTypes((prev) => {
                              const next = new Set(prev);
                              if (next.has(typeName)) next.delete(typeName);
                              else next.add(typeName);
                              return next;
                            });
                          }}
                        />
                        <span className="text-sm flex-1">{typeName}</span>
                        <span className="text-xs text-slate-500">{count} products</span>
                      </label>
                    );
                  })
                )}
              </div>
              {exportError ? <div className="text-sm text-danger">{exportError}</div> : null}
            </Dialog.Description>
            <Dialog.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setExportModalOpen(false);
                  setExportError("");
                }}
                disabled={exporting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleExportReport}
                disabled={exporting || availableExportTypes.length === 0 || exportReportKinds.size === 0}
              >
                {exporting ? (
                  <>
                    <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Lucide icon="Download" className="w-4 h-4 mr-2" />
                    Download Excel
                  </>
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
        <Dialog
          open={receiptModalOpen}
          onClose={() => {
            if (receiptVerifying) return;
            setReceiptModalOpen(false);
            setReceiptError("");
            setReceiptDetails(null);
          }}
        >
          <Dialog.Panel className="max-w-3xl">
            <Dialog.Title className="flex items-center justify-between">
              <div className="font-medium">Bank-In Receipt Details</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-3">
              {receiptError ? <div className="text-sm text-danger">{receiptError}</div> : null}
              {receiptDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Team:</span> {receiptDetails.teamName || "-"}</div>
                    <div><span className="text-slate-500">Sales Date:</span> {formatDateHeading(receiptDetails.salesDate)}</div>
                    <div><span className="text-slate-500">Submitted At:</span> {formatDateTime(receiptDetails.bankedAt)}</div>
                    <div><span className="text-slate-500">Receipts:</span> {receiptDetails.receiptCount}</div>
                    <div><span className="text-slate-500">Bank In Amount:</span> {receiptDetails.bankInAmount === null ? "-" : moneyWithThousands(receiptDetails.bankInAmount)}</div>
                    <div><span className="text-slate-500">Difference:</span> {moneyWithThousands(receiptDetails.bankInDifference || 0)}</div>
                    <div className="sm:col-span-2">
                      <span className="text-slate-500">Verification:</span>{" "}
                      {receiptDetails.bankInVerified ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                          <Lucide icon="BadgeCheck" className="w-4 h-4" />
                          Verified
                          {receiptDetails.bankInVerifiedByName ? ` by ${receiptDetails.bankInVerifiedByName}` : ""}
                          {receiptDetails.bankInVerifiedAt ? ` · ${formatDateTime(receiptDetails.bankInVerifiedAt)}` : ""}
                        </span>
                      ) : (
                        <span className="text-slate-600">Not verified</span>
                      )}
                    </div>
                  </div>
                  {receiptDetails.remark || (receiptDetails.reasons && receiptDetails.reasons.length > 0) ? (
                    <div className="space-y-2">
                      {receiptDetails.remark ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                          <div className="text-xs font-medium text-amber-800 mb-0.5">Remark</div>
                          <div className="text-slate-800 whitespace-pre-wrap">{receiptDetails.remark}</div>
                        </div>
                      ) : null}
                      {receiptDetails.reasons && receiptDetails.reasons.length > 0 ? (
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                          <div className="text-xs font-medium text-slate-500 mb-1">
                            Reason{receiptDetails.reasons.length > 1 ? "s" : ""} stated by supervisor
                          </div>
                          <div className="space-y-2">
                            {receiptDetails.reasons.map((log, idx) => (
                              <div key={`${log.createdAt}-${idx}`}>
                                <div className="text-slate-800 whitespace-pre-wrap">{log.reason}</div>
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                  {log.supervisorName ? `${log.supervisorName} · ` : ""}
                                  {formatDateTime(log.createdAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div>
                    <div className="text-sm font-medium mb-2">
                      Bank-In / Transfer Slip Payments {receiptDetails.payments?.length ? `(${receiptDetails.payments.length})` : ""}
                    </div>
                    {receiptLoading && (!receiptDetails.payments || receiptDetails.payments.length === 0) ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                        Loading slip images...
                      </div>
                    ) : !receiptDetails.payments || receiptDetails.payments.length === 0 ? (
                      <div className="text-sm text-slate-500">No payment record found.</div>
                    ) : (
                      <div className="space-y-3">
                        {receiptDetails.payments.map((p, idx) => (
                          <div key={p.id} className="border border-slate-200 rounded-md p-3">
                            <div className="flex items-center justify-between text-sm">
                              <div className="font-semibold text-slate-900">
                                Payment #{idx + 1} · {moneyWithThousands(p.amount)}
                              </div>
                              <div className="text-xs text-slate-500">{formatDateTime(p.createdAt)}</div>
                            </div>
                            {p.supervisorName ? (
                              <div className="mt-0.5 text-xs text-slate-500">By {p.supervisorName}</div>
                            ) : null}
                            {p.remark ? (
                              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm">
                                <div className="text-[11px] font-medium text-slate-500 mb-0.5">Remark</div>
                                <div className="text-slate-800 whitespace-pre-wrap">{p.remark}</div>
                              </div>
                            ) : null}
                            {p.bankSlips.length === 0 ? (
                              <div className="mt-2 text-xs text-slate-500">No receipt image for this payment.</div>
                            ) : (
                              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {p.bankSlips.map((s) => (
                                  <a
                                    key={s.id}
                                    href={s.imageUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block border border-slate-200 rounded-md overflow-hidden hover:border-primary/60"
                                  >
                                    <img
                                      src={s.imageUrl}
                                      alt="Bank receipt"
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-32 object-contain bg-slate-50"
                                    />
                                    <div className="px-2 py-1 text-[10px] text-slate-500">{formatDateTime(s.createdAt)}</div>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : receiptLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                  Loading receipt details...
                </div>
              ) : (
                <div className="text-sm text-slate-500">No receipt details available.</div>
              )}
            </Dialog.Description>
            <Dialog.Footer className="flex items-center justify-end gap-3">
              {receiptDetails &&
              !receiptDetails.bankInVerified &&
              (Number.isFinite(Number(receiptDetails.remainingAmount))
                ? Number(receiptDetails.remainingAmount) <= SETTLED_REMAINING_EPS
                : Math.max(0, -Number(receiptDetails.bankInDifference || 0)) <= SETTLED_REMAINING_EPS) ? (
                <Button
                  variant="success"
                  className="text-white"
                  onClick={() => void verifyReceiptBankIn()}
                  disabled={receiptLoading || receiptVerifying}
                >
                  {receiptVerifying ? (
                    <>
                      <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lucide icon="BadgeCheck" className="w-4 h-4 mr-2" />
                      Verify
                    </>
                  )}
                </Button>
              ) : null}
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setReceiptModalOpen(false);
                  setReceiptError("");
                  setReceiptDetails(null);
                  setReceiptVerifying(false);
                }}
                disabled={receiptVerifying}
              >
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      </RoleGate>
    </ProtectedRoute>
  );
}

